import { put, call } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import { t } from 'src/i18n';
import notifications from 'src/redux/notifications/actions';
import { deleteCard } from '../api';
import { RATE_CARD_HTTP } from '../constants';

// Without it a 403 is answered with the helper's own toast and nothing returned, so the reason the
// backend gave never reaches the screen.
const PRESERVE_STATUS = { preserveErrorStatus: true };

/**
 * Deletes one draft card and says whether it is gone, toasting the reason itself when it is not. The
 * list and the card page ask identically and diverge only afterwards, so the refusals are read here.
 */
export function* requestCardDelete(id: string): Generator<any, boolean, any> {
  try {
    // `preserveErrorStatus` keeps a 403 to one toast carrying the backend's own sentence: without it the
    // helper shows a generic refusal and returns nothing, and this would add "try again" on top of it.
    const result = yield call(sendRequest, deleteCard, { id }, undefined, undefined, undefined, PRESERVE_STATUS);

    // A range rather than `=== 200`, in case the endpoint ever stops sending a body. The transport parses
    // every response as JSON, so a bodiless 2xx cannot reach here today: headroom, not a live path.
    const succeeded = typeof result?.status === 'number' && result.status >= 200 && result.status < 300;

    // A card already gone is the outcome asked for, so both callers proceed. Logged rather than trusted
    // silently: an undeployed route and a card someone else deleted arrive identically.
    if (result?.status === RATE_CARD_HTTP.NOT_FOUND) {
      logger.warn(new Error(`rate card ${id}: delete answered 404, treating the card as already gone`));

      return true;
    }

    if (succeeded) return true;

    yield put(
      notifications.requestError({
        error: result?.message || t('dash.admin.rateCards.errors.delete_failed'),
      }),
    );

    return false;
  } catch (error) {
    logger.error(error);
    yield put(notifications.requestError({ error: t('dash.admin.rateCards.errors.delete_failed') }));

    return false;
  }
}
